import { isProject } from "Utils/IsType";
import { getItemType, updateProjectItem } from "Utils/Item";
import {
  useCallback, useContext, useEffect, useMemo, useState
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { uiSetActiveEditItemId, projectsUpdateStart } from "Store/Actions";
import { selectActiveEditItem, selectActiveProject } from "Store/Selectors/Projects";
import { isTrue } from "Utils/Boolean";
import { useAuthenticationPreflight } from "Components/Generic/Authentication";
import { getMissingFields } from "Utils/Project";
import { ConfigurationContext } from "Components/Generic/Configuration";
import { hasNoElements } from "Utils/Array";
import { IUseEdit } from "./IUseEdit";

export function useEdit(): IUseEdit {
  const dispatch = useDispatch();
  const config = useContext(ConfigurationContext);
  const { preflightAuthentication } = useAuthenticationPreflight();
  const project = useSelector(selectActiveProject);
  const activeEditItem = useSelector(selectActiveEditItem);
  const type = getItemType(activeEditItem);
  const [item, setUpdatedItem] = useState(activeEditItem);

  const hasRequiredFields = useMemo(
    () => hasNoElements(getMissingFields(item, config.getRequiredFields(type))?.missingFields),
    [item, config, type]
  );

  const allowSave = item != null //&& hasRequiredFields;

  useEffect(() => {
    setUpdatedItem(activeEditItem);
  }, [activeEditItem, project]);

  const onChange = useCallback(
    (fieldName: string, value: unknown) => {
      setUpdatedItem({ ...item, [fieldName]: value });
    },
    [item]
  );

  const internalOnSave = useCallback(
    async (withExport = false) => {
      await preflightAuthentication();
      dispatch(uiSetActiveEditItemId(null));
      if (item != null) {
        dispatch(
          projectsUpdateStart(
            [updateProjectItem(project, item[`@id`], (itm) => ({ ...itm, ...item }))],
            true,
            withExport
          )
        );
      }
      setUpdatedItem(activeEditItem);
    },
    [preflightAuthentication, dispatch, item, activeEditItem, project]
  );

  const onSave = useCallback(() => internalOnSave(false), [internalOnSave]);
  const onSaveExport = useCallback(() => internalOnSave(true), [internalOnSave]);

  return {
    isOpen: activeEditItem != null,
    item,
    onChange,
    onSave: allowSave ? onSave : null,
    onSaveExport: allowSave ? onSaveExport : null,
    type,
    useExport: isProject(activeEditItem) && isTrue(activeEditItem?.[`@isExported`], false)
  };
}
